import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

import type { IconComponent } from '@/shared/registry/types';
import { cn } from '@/shared/lib/cn';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/ui/breadcrumb';

import { useDocumentTitle } from './useDocumentTitle';

export interface BreadcrumbEntry {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  icon?: IconComponent;
  breadcrumbs?: BreadcrumbEntry[];
  /** Buttons on the right (primary action last). */
  actions?: ReactNode;
  /** Badges / meta next to the title. */
  meta?: ReactNode;
  /** Tabs or filters row under the title. */
  children?: ReactNode;
  /** Set `document.title` (default true). */
  setDocumentTitle?: boolean;
  className?: string;
}

/**
 * Standard page header: breadcrumbs, title (+icon/meta), description, actions and an optional row for tabs.
 * Every module page starts with one so pages look consistent.
 */
export function PageHeader({
  title,
  description,
  icon: Icon,
  breadcrumbs,
  actions,
  meta,
  children,
  setDocumentTitle = true,
  className,
}: PageHeaderProps) {
  useDocumentTitle(setDocumentTitle ? title : null);
  return (
    <header data-slot="page-header" className={cn('mb-6 space-y-4', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => (
              <Fragment key={`${crumb.label}-${index}`}>
                {index > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {crumb.to ? (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.to}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border bg-card text-brand-text shadow-xs">
              <Icon className="size-[1.125rem]" />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-[1.375rem] leading-tight font-semibold tracking-[-0.02em] text-foreground">
                {title}
              </h1>
              {meta}
            </div>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}
